import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconButton } from '@wearenotch/ui-react';
import styles from './Header.module.css';

interface HeaderProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

export function Header({ theme, onToggleTheme }: HeaderProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Transparent over the page glow at the top, solid once the page scrolls
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const isPatterns =
    location.pathname.startsWith('/category') || location.pathname.startsWith('/pattern');

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <div className={`container ${styles.inner}`}>
        <Link to="/" className={styles.logo}>
          <span className={styles.logoAx}>AX</span>
          <span className={styles.logoText}>Pattern Library</span>
        </Link>

        <nav className={styles.nav} aria-label="Main navigation">
          <Link
            to="/#patterns"
            className={`${styles.navLink} ${isPatterns ? styles.active : ''}`}
          >
            Patterns
          </Link>
          <Link to="/#approach" className={styles.navLink}>
            About AX
          </Link>
          <Link
            to="/glossary"
            className={`${styles.navLink} ${location.pathname.startsWith('/glossary') ? styles.active : ''}`}
          >
            Glossary
          </Link>
          <Link
            to="/compare"
            className={`${styles.navLink} ${location.pathname.startsWith('/compare') ? styles.active : ''}`}
          >
            Compare
          </Link>
        </nav>

        <div className={styles.actions}>
          <Link
            to="/quiz"
            className={`${styles.cta} ${location.pathname.startsWith('/quiz') ? styles.ctaActive : ''}`}
          >
            Find your pattern
          </Link>
          <IconButton
            icon={theme === 'light' ? <MoonIcon /> : <SunIcon />}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            variant="tertiary"
            size="sm"
            onClick={onToggleTheme}
            className={styles.themeToggle}
          />
        </div>
      </div>
    </header>
  );
}
